import type { UserManagerSettings } from 'oidc-client-ts';
import { API_BASE } from '@/api/client';

/**
 * Auth configuration, fetched from the service at boot.
 *
 * The realm is a *runtime* fact, not a build-time one. Baking it into the
 * bundle means pointing the workbench at a different realm requires rebuilding
 * and republishing the UI image, and the published image is silently wrong
 * everywhere except where it was built.
 *
 * `GET /api/v1/auth/config` is deliberately unauthenticated: it is the document
 * a browser needs *before* it has a token, the same role OIDC's discovery
 * document plays, and it carries nothing secret.
 *
 * There is no hardcoded realm fallback. A wrong default that happens to work in
 * one environment is worse than none, because it fails silently.
 */

export type AuthMode = 'mock' | 'oidc' | 'password';

/** Wire shape of `GET /api/v1/auth/config`. */
export interface AuthConfigDto {
  readonly mode?: string;
  readonly realm?: string;
  readonly issuerUri?: string;
  readonly loginUrl?: string;
  readonly refreshUrl?: string;
  readonly appType?: string;
  readonly clientId?: string;
  readonly scope?: string;
}

export interface AuthConfig {
  readonly mode: AuthMode;
  readonly realm: string;
  readonly issuerUri: string;
  readonly loginUrl: string;
  /** Null when the realm exposes no refresh endpoint (server sends ""). */
  readonly refreshUrl: string | null;
  readonly appType: string;
  readonly clientId: string;
  readonly scope: string;
}

export const AUTH_CONFIG_PATH = '/auth/config';

interface RawEnv {
  readonly VITE_AUTH_MODE?: string;
  readonly VITE_OIDC_AUTHORITY?: string;
  readonly VITE_OIDC_CLIENT_ID?: string;
  readonly VITE_OIDC_SCOPE?: string;
  readonly VITE_AUTH_LOGIN_URL?: string;
  readonly VITE_AUTH_REFRESH_URL?: string;
  readonly VITE_AUTH_APP_TYPE?: string;
  readonly DEV?: boolean;
}

function env(): RawEnv {
  // import.meta.env is untyped by Vite; narrowing it once here keeps the rest
  // of the app honest.
  return import.meta.env as unknown as RawEnv;
}

function asMode(value: string | undefined): AuthMode | null {
  return value === 'mock' || value === 'oidc' || value === 'password' ? value : null;
}

/** True when a developer has explicitly pinned a mode for local work. */
export function hasLocalModeOverride(): boolean {
  return asMode(env().VITE_AUTH_MODE) !== null;
}

function blankToNull(value: string | undefined): string | null {
  const trimmed = (value ?? '').trim();
  return trimmed === '' ? null : trimmed;
}

/**
 * Merges the local `VITE_*` development overrides over the server's config.
 * Overrides exist for working against a realm the local backend does not know
 * about; in a deployed image none of them are set.
 */
export function applyOverrides(base: AuthConfig): AuthConfig {
  const e = env();
  return {
    mode: asMode(e.VITE_AUTH_MODE) ?? base.mode,
    realm: base.realm,
    issuerUri: blankToNull(e.VITE_OIDC_AUTHORITY) ?? base.issuerUri,
    loginUrl: blankToNull(e.VITE_AUTH_LOGIN_URL) ?? base.loginUrl,
    refreshUrl: blankToNull(e.VITE_AUTH_REFRESH_URL) ?? base.refreshUrl,
    appType: blankToNull(e.VITE_AUTH_APP_TYPE) ?? base.appType,
    clientId: blankToNull(e.VITE_OIDC_CLIENT_ID) ?? base.clientId,
    scope: blankToNull(e.VITE_OIDC_SCOPE) ?? base.scope,
  };
}

export function normalizeAuthConfig(dto: AuthConfigDto): AuthConfig {
  return {
    mode: asMode(dto.mode) ?? 'password',
    realm: dto.realm ?? '',
    issuerUri: dto.issuerUri ?? '',
    loginUrl: dto.loginUrl ?? '',
    refreshUrl: blankToNull(dto.refreshUrl),
    appType: dto.appType ?? 'Admin',
    clientId: dto.clientId ?? '',
    scope: dto.scope ?? 'openid profile email',
  };
}

/** Config derived purely from local overrides, for development without a backend. */
export function overrideOnlyConfig(): AuthConfig {
  return applyOverrides({
    mode: 'mock',
    realm: 'local',
    issuerUri: '',
    loginUrl: '',
    refreshUrl: null,
    appType: 'Admin',
    clientId: '',
    scope: 'openid profile email',
  });
}

export class AuthConfigError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'AuthConfigError';
  }
}

let cached: Promise<AuthConfig> | null = null;

async function requestAuthConfig(): Promise<AuthConfig> {
  let response: Response;
  try {
    // Plain fetch, not apiFetch: this runs before any token exists and must not
    // be entangled with the 401 handling that assumes one.
    response = await fetch(`${API_BASE}${AUTH_CONFIG_PATH}`, {
      headers: { Accept: 'application/json' },
    });
  } catch (cause) {
    throw new AuthConfigError(
      `Cannot reach the service to load sign-in settings (${
        cause instanceof Error ? cause.message : 'network error'
      }).`,
    );
  }

  if (!response.ok) {
    throw new AuthConfigError(
      `The service returned ${response.status} for its sign-in settings.`,
    );
  }

  try {
    return normalizeAuthConfig((await response.json()) as AuthConfigDto);
  } catch {
    throw new AuthConfigError('The service returned malformed sign-in settings.');
  }
}

/**
 * Resolves auth config once per page load.
 *
 * On failure this rejects rather than falling back to `mock`: quietly dropping
 * to a permissive mode because the network hiccuped is the wrong direction to
 * fail. The one exception is an explicit `VITE_AUTH_MODE`, which is the
 * documented local-development override and requires a deliberate build-time
 * choice.
 */
export function loadAuthConfig(): Promise<AuthConfig> {
  if (!cached) {
    cached = requestAuthConfig()
      .then(applyOverrides)
      .catch((cause: unknown) => {
        if (hasLocalModeOverride()) {
          console.warn(
            '[auth] Could not load /api/v1/auth/config; using the local VITE_AUTH_MODE override.',
            cause,
          );
          return overrideOnlyConfig();
        }
        cached = null; // Allow a retry.
        throw cause;
      });
  }
  return cached;
}

/** Test hook: forget the page-lifetime cache. */
export function __resetAuthConfigCache(): void {
  cached = null;
}

export function oidcSettings(config: AuthConfig): UserManagerSettings {
  return {
    authority: config.issuerUri,
    client_id: config.clientId,
    redirect_uri: `${window.location.origin}/auth/callback`,
    post_logout_redirect_uri: window.location.origin,
    silent_redirect_uri: `${window.location.origin}/auth/silent`,
    response_type: 'code',
    scope: config.scope,
    automaticSilentRenew: true,
    // Access tokens are short-lived; renew a minute before expiry so an
    // in-flight query never dies on a 401.
    accessTokenExpiringNotificationTimeInSeconds: 60,
    monitorSession: false,
    loadUserInfo: true,
  };
}
